// frontend/src/components/Project/ProjectHistory.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  Button,
  Divider,
  Tooltip,
  IconButton
} from '@mui/material';
import {
  History as HistoryIcon,
  CalendarToday as CalendarIcon,
  Flag as FlagIcon,
  Lock as LockIcon,
  Visibility as VisibilityIcon,
  Info as InfoIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { alpha, styled } from '@mui/material/styles';
import SprintHistoryDetailModal from './SprintHistoryDetailModal';

const sprintAccents = [
  {
    main: '#2196F3',
    soft: '#64B5F6',
    tint: '#E3F2FD'
  },
  {
    main: '#00A896',
    soft: '#5ED6C5',
    tint: '#E0F7F3'
  },
  {
    main: '#7357E8',
    soft: '#A994FF',
    tint: '#EEE9FF'
  },
  {
    main: '#0EA5B7',
    soft: '#63D5E2',
    tint: '#E0F7FA'
  },
  {
    main: '#9C5DE5',
    soft: '#D18CF4',
    tint: '#F4E9FF'
  }
];

const getSprintAccent = (index) => sprintAccents[index % sprintAccents.length];

const ScrollContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  overflowX: 'auto',
  overflowY: 'hidden',
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 1, 2.5),
  scrollBehavior: 'smooth',
  scrollSnapType: 'x proximity',
  '&::-webkit-scrollbar': {
    height: '8px'
  },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.common.white, 0.32),
    borderRadius: '999px'
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.primary.main, 0.28),
    borderRadius: '999px',
    '&:hover': {
      background: alpha(theme.palette.primary.main, 0.48)
    }
  }
}));

const SprintCard = styled(Card, { shouldForwardProp: (prop) => prop !== 'accent' })(({ theme, accent }) => ({
  minWidth: 286,
  maxWidth: 286,
  minHeight: 300,
  borderRadius: theme.spacing(3),
  flexShrink: 0,
  scrollSnapAlign: 'start',
  position: 'relative',
  overflow: 'hidden',
  cursor: 'pointer',
  border: `1px solid ${alpha(theme.palette.common.white, 0.82)}`,
  background: `
    linear-gradient(145deg,
      ${alpha(theme.palette.common.white, 0.74)},
      ${alpha(accent.tint, 0.70)} 52%,
      ${alpha(accent.soft, 0.18)}
    ),
    radial-gradient(circle at 88% 8%, ${alpha(accent.soft, 0.34)}, transparent 34%),
    radial-gradient(circle at 8% 92%, ${alpha(accent.main, 0.13)}, transparent 38%)
  `,
  backgroundColor: alpha(theme.palette.common.white, 0.54),
  backdropFilter: 'blur(22px) saturate(145%)',
  WebkitBackdropFilter: 'blur(22px) saturate(145%)',
  boxShadow: `0 16px 42px ${alpha(accent.main, 0.10)}`,
  transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 5,
    background: `linear-gradient(90deg, ${accent.main}, ${accent.soft})`,
    opacity: 0.9
  },
  '&:hover': {
    transform: 'translateY(-5px)',
    borderColor: alpha(accent.soft, 0.82),
    boxShadow: `0 22px 52px ${alpha(accent.main, 0.18)}`
  },
  [theme.breakpoints.down('sm')]: {
    minWidth: 274,
    maxWidth: 274
  }
}));

const HistoryContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(4),
  border: `1px solid ${alpha(theme.palette.common.white, 0.86)}`,
  background: `
    linear-gradient(145deg,
      ${alpha(theme.palette.common.white, 0.68)},
      ${alpha(theme.palette.grey[100], 0.42)} 48%,
      ${alpha(theme.palette.primary.main, 0.10)}
    ),
    radial-gradient(circle at 8% 8%, ${alpha(theme.palette.info.light, 0.18)}, transparent 35%),
    radial-gradient(circle at 92% 92%, ${alpha(theme.palette.primary.main, 0.16)}, transparent 38%)
  `,
  backgroundColor: alpha(theme.palette.common.white, 0.48),
  backdropFilter: 'blur(28px) saturate(145%)',
  WebkitBackdropFilter: 'blur(28px) saturate(145%)',
  boxShadow: `0 24px 80px ${alpha(theme.palette.common.black, 0.10)}`
}));

const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(5, 2),
  color: theme.palette.text.secondary
}));

const ArrowButton = styled(IconButton)(({ theme }) => ({
  width: 46,
  height: 46,
  borderRadius: '50%',
  border: `1px solid ${alpha(theme.palette.common.white, 0.88)}`,
  backgroundColor: alpha(theme.palette.common.white, 0.58),
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  boxShadow: `0 10px 28px ${alpha(theme.palette.common.black, 0.10)}`,
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.white, 0.82),
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.16)}`
  }
}));

const ProjectHistory = ({ sprints = [], isViewOnly = false }) => {
  const [selectedSprint, setSelectedSprint] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const scrollContainerRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  const completedSprints = sprints.filter(s => s.status === 'completed');

  useEffect(() => {
    const checkScroll = () => {
      const container = scrollContainerRef.current;
      if (container) {
        setShowLeftArrow(container.scrollLeft > 0);
        setShowRightArrow(
          container.scrollLeft < container.scrollWidth - container.clientWidth - 10
        );
      }
    };

    checkScroll();
    window.addEventListener('resize', checkScroll);

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScroll);
    }

    return () => {
      window.removeEventListener('resize', checkScroll);
      if (container) {
        container.removeEventListener('scroll', checkScroll);
      }
    };
  }, [completedSprints.length]);

  const scrollLeft = () => {
    scrollContainerRef.current?.scrollBy({ left: -310, behavior: 'smooth' });
  };

  const scrollRight = () => {
    scrollContainerRef.current?.scrollBy({ left: 310, behavior: 'smooth' });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указана';
    try {
      return new Date(dateString).toLocaleDateString('ru-RU', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch (e) {
      return 'Не указана';
    }
  };

  const handleSprintClick = (sprint) => {
    if (isViewOnly) return;
    setSelectedSprint(sprint);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSprint(null);
  };

  return (
    <>
      <HistoryContainer elevation={0}>
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            mb: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '50%',
                color: '#fff',
                background: `linear-gradient(145deg, ${sprintAccents[0].main}, ${sprintAccents[0].soft})`,
                boxShadow: `0 10px 26px ${alpha(sprintAccents[0].main, 0.22)}`
              }}
            >
              <HistoryIcon />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{ color: 'text.primary', lineHeight: 1.15 }}
              >
                История завершённых спринтов
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                Здесь собраны все завершённые спринты проекта. Нажмите на карточку, чтобы посмотреть детали.
              </Typography>
            </Box>
          </Box>

          <Chip
            icon={<CheckCircleIcon />}
            label={`${completedSprints.length} завершённых`}
            sx={{
              flexShrink: 0,
              borderRadius: 999,
              height: 36,
              color: sprintAccents[1].main,
              fontWeight: 700,
              backgroundColor: alpha(sprintAccents[1].soft, 0.18),
              border: `1px solid ${alpha(sprintAccents[1].soft, 0.42)}`,
              '& .MuiChip-icon': {
                color: sprintAccents[1].main
              }
            }}
          />

          {isViewOnly && (
            <Tooltip title="Режим только для просмотра">
              <LockIcon
                fontSize="small"
                sx={{ color: 'text.disabled', opacity: 0.6 }}
              />
            </Tooltip>
          )}
        </Box>

        {completedSprints.length === 0 ? (
          <EmptyState>
            <InfoIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2, opacity: 0.5 }} />
            <Typography variant="body1" gutterBottom fontWeight={600}>
              Нет завершённых спринтов
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Завершённые спринты будут отображаться здесь
            </Typography>
          </EmptyState>
        ) : (
          <Box sx={{ position: 'relative' }}>
            {showLeftArrow && (
              <ArrowButton
                onClick={scrollLeft}
                aria-label="Предыдущие спринты"
                sx={{
                  position: 'absolute',
                  left: -14,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 3
                }}
              >
                <ChevronLeftIcon />
              </ArrowButton>
            )}

            {showRightArrow && (
              <ArrowButton
                onClick={scrollRight}
                aria-label="Следующие спринты"
                sx={{
                  position: 'absolute',
                  right: -14,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  zIndex: 3
                }}
              >
                <ChevronRightIcon />
              </ArrowButton>
            )}

            <ScrollContainer ref={scrollContainerRef}>
              {completedSprints.map((sprint, index) => {
                const accent = getSprintAccent(index);

                return (
                  <SprintCard
                    key={sprint.id}
                    accent={accent}
                    onClick={() => handleSprintClick(sprint)}
                    sx={{
                      opacity: isViewOnly ? 0.92 : 1,
                      cursor: isViewOnly ? 'default' : 'pointer',
                      '&:hover': isViewOnly
                        ? {
                            transform: 'none',
                            boxShadow: `0 16px 42px ${alpha(accent.main, 0.10)}`
                          }
                        : undefined
                    }}
                  >
                    <CardContent
                      sx={{
                        p: 2.25,
                        pt: 2.7,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        boxSizing: 'border-box'
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1.6
                        }}
                      >
                        <Chip
                          icon={<CheckCircleIcon sx={{ fontSize: '16px !important' }} />}
                          label="ЗАВЕРШЁН"
                          size="small"
                          sx={{
                            height: 28,
                            borderRadius: 999,
                            color: accent.main,
                            fontWeight: 800,
                            letterSpacing: 0.25,
                            backgroundColor: alpha(accent.soft, 0.22),
                            border: `1px solid ${alpha(accent.soft, 0.44)}`,
                            '& .MuiChip-icon': {
                              color: accent.main
                            }
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', fontWeight: 700 }}
                        >
                          #{index + 1}
                        </Typography>
                      </Box>

                      <Typography
                        variant="h6"
                        fontWeight={800}
                        sx={{
                          color: 'text.primary',
                          lineHeight: 1.2,
                          mb: 1.2
                        }}
                      >
                        {sprint.title || `Спринт ${sprint.id}`}
                      </Typography>

                      {sprint.goal && (
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.5,
                            minHeight: 64,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {sprint.goal}
                        </Typography>
                      )}

                      <Box sx={{ mt: 'auto' }}>
                        <Divider
                          sx={{
                            my: 1.7,
                            borderColor: alpha(accent.main, 0.14)
                          }}
                        />

                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            mb: 1
                          }}
                        >
                          <CalendarIcon sx={{ fontSize: 18, color: accent.main }} />
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', fontWeight: 600 }}
                          >
                            {formatDate(sprint.start_date)} — {formatDate(sprint.end_date)}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <FlagIcon sx={{ fontSize: 18, color: accent.main }} />
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', fontWeight: 600 }}
                          >
                            {sprint.duration_days || '?'} дней
                          </Typography>
                        </Box>

                        {!isViewOnly ? (
                          <Button
                            fullWidth
                            variant="text"
                            size="small"
                            endIcon={<VisibilityIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSprintClick(sprint);
                            }}
                            sx={{
                              mt: 1.8,
                              minHeight: 40,
                              borderRadius: 999,
                              textTransform: 'none',
                              fontWeight: 750,
                              color: accent.main,
                              backgroundColor: alpha(accent.soft, 0.12),
                              border: `1px solid ${alpha(accent.soft, 0.26)}`,
                              '&:hover': {
                                backgroundColor: alpha(accent.soft, 0.22)
                              }
                            }}
                          >
                            Подробнее
                          </Button>
                        ) : (
                          <Box sx={{ mt: 1.8, textAlign: 'center' }}>
                            <Chip
                              size="small"
                              icon={<LockIcon fontSize="small" />}
                              label="Только просмотр"
                              sx={{
                                borderRadius: 999,
                                color: 'text.secondary',
                                backgroundColor: 'rgba(255,255,255,0.36)'
                              }}
                            />
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </SprintCard>
                );
              })}
            </ScrollContainer>
          </Box>
        )}
      </HistoryContainer>

      <SprintHistoryDetailModal
        open={modalOpen}
        onClose={handleCloseModal}
        sprint={selectedSprint}
        isViewOnly={isViewOnly}
      />
    </>
  );
};

export default ProjectHistory;
